import type { ChatContext } from '@/api/hooks';

const UUID = '([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})';
const SUBJECTS: Array<[RegExp, (id: string) => ChatContext]> = [
  [new RegExp(`^/mail/${UUID}$`), (id) => ({ page: 'thread', threadId: id })],
  [new RegExp(`^/companies/${UUID}$`), (id) => ({ page: 'company', companyId: id })],
  [new RegExp(`^/contacts/${UUID}$`), (id) => ({ page: 'contact', contactId: id })],
  [new RegExp(`^/projects/${UUID}$`), (id) => ({ page: 'project', projectId: id })],
  [new RegExp(`^/prospects/${UUID}$`), (id) => ({ page: 'prospect', prospectId: id })],
  [new RegExp(`^/profiles/${UUID}$`), (id) => ({ page: 'profile', profileId: id })],
  [new RegExp(`^/territories/${UUID}$`), (id) => ({ page: 'territory', territoryId: id })],
  [new RegExp(`^/prospecting/plans/${UUID}$`), (id) => ({ page: 'plan', planId: id })],
  [new RegExp(`^/suppliers/${UUID}$`), (id) => ({ page: 'supplier', supplierId: id })],
  [new RegExp(`^/offers/${UUID}$`), (id) => ({ page: 'offer', offerId: id })],
  [new RegExp(`^/signals/${UUID}$`), (id) => ({ page: 'signal', signalId: id })],
  [new RegExp(`^/buyers/${UUID}$`), (id) => ({ page: 'buyer', buyerId: id })],
  [new RegExp(`^/tasks/${UUID}$`), (id) => ({ page: 'task', taskId: id })],
  [new RegExp(`^/institutions/${UUID}$`), (id) => ({ page: 'institution', programmeId: id })],
  [new RegExp(`^/today/events/${UUID}$`), (id) => ({ page: 'calendar', calendarEventId: id })],
  [new RegExp(`^/today/briefs/${UUID}$`), (id) => ({ page: 'brief', briefId: id })],
  [new RegExp(`^/requests/${UUID}$`), (id) => ({ page: 'request', requestId: id })],
  [new RegExp(`^/incidents/${UUID}$`), (id) => ({ page: 'incident', incidentId: id })],
  [new RegExp(`^/prospecting/shortlists/${UUID}$`), (id) => ({ page: 'shortlist', shortlistId: id })],
  [new RegExp(`^/profile-changes/${UUID}$`), (id) => ({ page: 'profile_change', profileChangeId: id })],
  [new RegExp(`^/introductions/${UUID}$`), (id) => ({ page: 'introduction', introductionId: id })],
  [new RegExp(`^/our-index/${UUID}$`), (id) => ({ page: 'index_entry', indexEntryId: id })],
  // A market is keyed by its country code, which the schema checks is two capitals.
  [/^\/markets\/([A-Z]{2})(?:\/report)?$/, (code) => ({ page: 'market', marketCode: code })],
];

/**
 * The identifiers the chat is told, from the page — never its words
 * (`chatContextSchema` is strict and refuses anything else). A record page
 * names its record; everywhere else the chat knows the department and the
 * day.
 */
export function contextFor(pathname: string, department: string): ChatContext {
  // The page's address too, so the agent is told what the page is for from
  // its help: an address only, which the schema checks carries no prose.
  const route = /^\/[A-Za-z0-9/_.-]{0,200}$/.test(pathname) ? { route: pathname } : {};
  for (const [pattern, make] of SUBJECTS) {
    const id = pattern.exec(pathname)?.[1];
    if (id) return { ...make(id), ...route };
  }
  return { page: 'today', department, ...route };
}
